import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useHistory, Redirect } from "react-router-dom";
import { isMustChangePassword } from "../services/researcherSecurity";
import styled, { createGlobalStyle } from "styled-components";
import Sidebar from "../components/Sidebar";
import Header from "../components/AppBar";
import Footer from "../components/Footer";
import { Alert } from "@mui/lab";
import { Translate } from 'react-localize-redux';
import { useSherwoodUser } from '../hooks';
import { spacing } from "@mui/system";
import { connect } from 'react-redux';
import { fetchInvestigations } from '../redux/actions/investigationsActions';
import { updateLoadingRecords } from '../redux/actions/offlineActions';
import { ROOT_ROUTE, HOSPITAL_HOME_ROUTE, CHANGE_PASSWORD_ROUTE, HOSPITAL_MY_CORRECTIONS } from "../routes/urls";
import { correctionShellRoute, isCorrectionOnlySession } from "../pages/hospital/MyCorrections/correctionAccess";
import WebSessionOutOfScopeNotice from "../pages/hospital/MyCorrections/WebSessionOutOfScopeNotice";
import { onWebSessionOutOfScope } from "../services/webSessionScope";
import {
  Hidden,
  CssBaseline,
  Paper as MuiPaper,

  Typography,
  CircularProgress,
} from "@mui/material";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import Loader from '../components/Loader';

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
const withWidth = () => (WrappedComponent) => (props) => <WrappedComponent {...props} width="xs" />;

const drawerWidth = 258;

const GlobalStyle = createGlobalStyle`
  html,
  body,
  #root {
    height: 100%;
  }

  body {
    background: ${(props) => props.theme.palette.background.default};
  }

  .MuiCardHeader-action .MuiIconButton-root {
    padding: 4px;
    width: 28px;
    height: 28px;
  }
`;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const LoadingScreen = styled.div`
  position:absolute;
  height:100%;
  width:100%;
  z-index:1301;
  opacity:0.7;
  background-color:#fff;
`;

const LoadingMessage = styled.div`
  position:absolute;
  display:flex;
  align-items: center;
  justify-content:center;
  vertical-align:center;
  height:100%;
  width:100%;
  z-index:1301;
`;
const LoadingContainer = styled.div`
    width:20rem;
    height:20rem;
    display:flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width:100%;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  
  flex: 1;
  background:${(props) => props.theme.palette.background.default}!important;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

const Dashboard = ({ children, routes, width, investigations, offline }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const {isLoading, error} = useSherwoodUser();
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    // SKIN-600: the API refused something outside a dermatologist's correction work. Not an
    // expiry — the session stays — so say why the screen is empty. Cleared on navigation.
    const [outOfScope, setOutOfScope] = useState(false);
    useEffect(() => onWebSessionOutOfScope(() => setOutOfScope(true)), []);
    useEffect(() => { setOutOfScope(false); }, [location.pathname]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };
    useEffect(() => {
        async function fetchRemoteInvestigations(){
            await dispatch(
                fetchInvestigations()
              );
        }
        if(!investigations.data){
            fetchRemoteInvestigations()
        }
        try{
          //const channel = new BroadcastChannel('sw-messages');
          //console.log(channel);
          //channel.addEventListener("message", async (event) => {
            
            
          navigator.serviceWorker.addEventListener('message', async event => {
              console.log("Message received", event);
              if(event.data.hasOwnProperty("updatingRecords")){
                  console.log(event.data.updatingRecords);
                  await dispatch( 
                      updateLoadingRecords(event.data.updatingRecords)
                  );
                  if(event.data.updatingRecords === false){
                    window.location.href = window.location.origin;
                  }
              }
          }, false);
        }
        catch(error){
          //postErrorSlack("", "BroadcastChannel not available", "");
          console.log("BroadcastChannel not available");
        }
        
    }, [])
    // Reset password v2: if the logged-in researcher just got their password reset
    // by an admin, every route except /change-password redirects to the forced-change
    // page until they pick a new password.
    if (isMustChangePassword() && location.pathname !== '/change-password') {
        return <Redirect to="/change-password" />;
    }

    if(isLoading){
        return <Loader />
    }
    else if(error){
        return(
            <Alert mb={4} severity="error">
                <Translate id="investigation.share.error.description" />
            </Alert>
        )
    }
    // Mobile-only roles (Dermatologist, Field Coordinator) lack WEB_ACCESS but must still
    // be able to log in and accept investigation invites. Allow the investigations list,
    // block everything else with a "go back to the app" message.
    const isMobileOnlyRole =
        import.meta.env.VITE_APP_PRODUCT === 'HOSPITAL' &&
        investigations.data &&
        investigations.data.length > 0 &&
        !investigations.data.some(inv => Array.isArray(inv.permissions) && inv.permissions.includes('WEB_ACCESS'));
    const onInvestigationsPage = location.pathname.startsWith('/investigations');
    // A pending invitation must stay reachable: HomeSchedule renders its
    // accept/deny screen on the home route, so don't block the home route while
    // the mobile-only user still has an invite to answer.
    const hasPendingInvitation =
        investigations.data && investigations.data.some(inv => inv.shareStatus === 0);
    const onHomeRoute =
        location.pathname === ROOT_ROUTE || location.pathname === HOSPITAL_HOME_ROUTE;
    // The forced password-change page is served inside this layout (line ~160
    // redirects here), so it must stay reachable even for mobile-only roles —
    // otherwise an admin-reset dermatologist can never set a new password on the
    // web. Exempt /change-password from the mobile-only block.
    const onChangePasswordRoute = location.pathname === CHANGE_PASSWORD_ROUTE;
    // SKIN-600: a dermatologist holds WEB_ACCESS since SKIN-599, but its web session is bounded
    // by the API to its correction work: it lands on My corrections and stays there and on the
    // patient page; in a study without WEB_CLINICAL_CORRECTION it gets the mobile-app screen.
    const correctionRoute = import.meta.env.VITE_APP_PRODUCT === 'HOSPITAL' && isCorrectionOnlySession(investigations.data)
        ? correctionShellRoute({ pathname: location.pathname, study: investigations.currentInvestigation, hasPendingInvitation: !!hasPendingInvitation })
        : 'allow';
    if (correctionRoute === 'panel') {
        return <Redirect to={HOSPITAL_MY_CORRECTIONS} />;
    }
    if (correctionRoute === 'home') {
        return <Redirect to={ROOT_ROUTE} />;
    }
    const blockMobileOnly = correctionRoute === 'block' ||
        (isMobileOnlyRole && !onInvestigationsPage && !onChangePasswordRoute && !(hasPendingInvitation && onHomeRoute));
    const showSidebar = !blockMobileOnly && ((investigations.data && investigations.currentInvestigation && import.meta.env.VITE_APP_PRODUCT === 'HOSPITAL') || (import.meta.env.VITE_APP_PRODUCT !== 'HOSPITAL'))
    return (
        <React.Fragment>
          {
            offline.loading &&
            <React.Fragment>
                <LoadingScreen />
                <LoadingMessage>
                  <LoadingContainer>
                      <Typography variant="subtitle1" color="textPrimary">
                          <Translate id="general.updating-records" />
                      </Typography>
                      <CircularProgress />  
                  </LoadingContainer>
                </LoadingMessage>
            </React.Fragment>
          }
            
            <Root>
                
                <CssBaseline />
                <GlobalStyle />
                <Drawer>
                    <Hidden mdUp implementation="js">
                    {
                        (showSidebar) &&
                        <Sidebar
                          routes={routes}
                          PaperProps={{ style: { width: drawerWidth } }}
                          variant="temporary"
                          investigation={investigations.currentInvestigation}
                          open={mobileOpen}
                          onClose={handleDrawerToggle}
                      />    
                    }
                    
                    </Hidden>
                    <Hidden mdDown implementation="css">
                    {
                        (showSidebar) &&
                        <Sidebar
                            routes={routes}
                            investigation={investigations.currentInvestigation}
                            PaperProps={{ style: { width: drawerWidth } }}
                        />
                    }
                    </Hidden>
                </Drawer>
                <AppContent>
                    <Header onDrawerToggle={handleDrawerToggle} />
                    <MainContent sx={{
    height: {
      xs: '30%',
      md: '100%',
    }
  }}>
                    {isMobileOnlyRole && onInvestigationsPage && (
                        <Alert severity="info" data-testid="mobile-only-banner" style={{ margin: 16 }}>
                            <Translate id="login.mobile-only-banner" options={{ onMissingTranslation: () => "Your role works in the SkincAIr mobile app. Use this page only to accept investigation invitations." }} />
                        </Alert>
                    )}
                    <WebSessionOutOfScopeNotice
                        open={outOfScope}
                        onClose={() => setOutOfScope(false)}
                        onBackToCorrections={() => history.push(HOSPITAL_MY_CORRECTIONS)}
                    />
                    {blockMobileOnly ? (
                        <div data-testid="no-web-access-block" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', padding: 24 }}>
                            <MuiPaper elevation={0} style={{ padding: 48, maxWidth: 460, textAlign: 'center', background: 'transparent' }}>
                                <PhoneAndroidIcon style={{ fontSize: 72, color: '#4FBFB1', marginBottom: 16 }} />
                                <Typography variant="h5" color="textPrimary" gutterBottom style={{ fontWeight: 600 }}>
                                    <Translate id="login.no-web-access-title" options={{ onMissingTranslation: () => "You're all set on mobile" }} />
                                </Typography>
                                <Typography variant="body1" color="textSecondary" style={{ marginTop: 8, lineHeight: 1.6 }}>
                                    <Translate id="login.no-web-access" options={{ onMissingTranslation: () => "Your work happens in the SkincAIr mobile app. Open it on your device to continue — patients, cases and visits are all there." }} />
                                </Typography>
                            </MuiPaper>
                        </div>
                    ) : children}
                    </MainContent>
                    <Footer />
                </AppContent>
                
            </Root> 
          </React.Fragment>
    );
};

const mapDispatchToProps = dispatch => {
    return {    
        fetchInvestigations : () => dispatch(fetchInvestigations())
    }
}

const mapStateToProps = (state) =>{
    return {
        investigations : state.investigations,
        offline: state.offline
    }
}
export default (connect(mapStateToProps, mapDispatchToProps)(Dashboard));
